import React, { useEffect, useState } from 'react';
import Section from '../forms/section/Section';
import Input from '../forms/input/Input';
import TextArea from '../forms/textArea/TextArea';
import Radio from '../forms/radio/Radio';
import PhoneInput from '../forms/phone-number-input/PhoneInput';
import DynamicSelect from '../forms/select/DynamicSelect';
import fieldVisibilityService from '../../services/fieldVisibilityService';
import { CheckCircle } from 'lucide-react';

const FormStep = ({ formData, handleInputChange, handleSubmit, handleFileChange, errors, emailVerified }) => {
    // État local pour afficher conditionnellement les champs de relance
    const [fieldVisibility, setFieldVisibility] = useState({
        datereclamationinitiale: false,
        modeintroductioninitiale: false,
        precisionagence: false,
        nomgestionnaire: false
    });

    useEffect(() => {
        const updateFieldVisibility = async () => {
            const visibility = await fieldVisibilityService.getFieldVisibility(formData);
            setFieldVisibility(prevState => ({
                ...prevState,
                ...visibility
            }));
        };

        updateFieldVisibility();
    }, [formData]);

    return (
        <form onSubmit={handleSubmit} className="p-4" noValidate>
            {/* Section 1: Informations Personnelles */}
            <Section title="Informations Personnelles">
                <div className="row g-3">
                    <div className="col-md-6">
                        <DynamicSelect
                            label="Civilité"
                            name="civilite"
                            categoryId="civilite" // ID de la catégorie dans la base de données
                            value={formData.civilite}
                            handleChange={handleInputChange}
                            error={errors.civilite}
                            required
                            tooltip="Veuillez sélectionner votre titre de civilité"
                        />
                    </div>
                    <div className="col-md-6">
                        <DynamicSelect
                            label="Ville"
                            name="ville"
                            categoryId="ville" // ID de la catégorie dans la base de données
                            value={formData.ville}
                            handleChange={handleInputChange}
                            error={errors.ville}
                            required
                            tooltip="Sélectionnez votre ville de résidence"
                        />
                    </div>

                    <div className="col-md-6">
                        <Input
                            label="Nom"
                            name="nom"
                            value={formData.nom}
                            handleChange={handleInputChange}
                            error={errors.nom}
                            required
                            tooltip="Lettres, espaces, tirets et apostrophes uniquement"
                            pattern="[a-zA-ZÀ-ÿ\s-']+"
                        />
                    </div>
                    <div className="col-md-6">
                        <Input
                            label="Prénom"
                            name="prenom"
                            value={formData.prenom}
                            handleChange={handleInputChange}
                            error={errors.prenom}
                            required
                            tooltip="Lettres, espaces, tirets et apostrophes uniquement"
                            pattern="[a-zA-ZÀ-ÿ\s-']+"
                        />
                    </div>

                    <div className="col-md-6">
                        <div className="form-group">
                            <label className="form-label d-flex align-items-center" htmlFor="email">
                                Email <span className="text-danger ms-1">*</span>
                                {emailVerified && (
                                    <span className="ms-2 text-success d-flex align-items-center">
                                        <CheckCircle size={16} className="me-1" />
                                        <small>Vérifié</small>
                                    </span>
                                )}
                            </label>
                            <input
                                type="email"
                                className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                required
                                readOnly={emailVerified}
                                style={emailVerified ? { backgroundColor: '#f8f9fa' } : {}}
                            />
                            {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                            <small className="form-text text-muted">Exemple: nom@domaine.com</small>
                        </div>
                    </div>

                    {!emailVerified && (
                        <div className="col-md-6">
                            <Input
                                label="Confirmation Email"
                                name="emailConfirm"
                                type="email"
                                value={formData.emailConfirm}
                                handleChange={handleInputChange}
                                error={errors.emailConfirm}
                                required
                            />
                        </div>
                    )}

                    <div className="col-md-6">
                        <PhoneInput
                            label="Téléphone"
                            name="telephone"
                            value={formData.telephone}
                            handleChange={handleInputChange}
                            error={errors.telephone}
                            required
                            tooltip="Entrez les 9 chiffres de votre numéro sénégalais"
                            placeholder="7XXXXXXXX"
                        />
                    </div>
                </div>
            </Section>

            {/* Section 2: Détails de la Réclamation */}
            <Section title="Détails de la Réclamation">
                <div className="row g-3">
                    <div className="col-md-6">
                        <DynamicSelect
                            label="Point de service"
                            name="pointService"
                            categoryId="pointService"
                            value={formData.pointService}
                            handleChange={handleInputChange}
                            error={errors.pointService}
                            required
                            tooltip="Agence ou service concerné par la réclamation"
                        />
                    </div>
                    <div className="col-md-6">
                        <Input
                            label="Numéro de compte"
                            name="numeroCompte"
                            value={formData.numeroCompte}
                            handleChange={handleInputChange}
                            error={errors.numeroCompte}
                            required
                            tooltip="Votre numéro de compte bancaire (11 à 34 caractères)"
                            pattern="[A-Z0-9]{11,34}"
                            helperText="Lettres majuscules et chiffres uniquement"
                        />
                    </div>

                    <div className="col-md-6">
                        <DynamicSelect
                            label="Canal de Réponse"
                            name="canalReponse"
                            categoryId="canalReponse"
                            value={formData.canalReponse}
                            handleChange={handleInputChange}
                            error={errors.canalReponse}
                            required
                            tooltip="Comment souhaitez-vous recevoir notre réponse"
                        />
                    </div>
                    <div className="col-md-6">
                        <Input
                            label="Lieu de l'incident"
                            name="lieuIncident"
                            value={formData.lieuIncident}
                            handleChange={handleInputChange}
                            error={errors.lieuIncident}
                            tooltip="Précisez le lieu où s'est produit l'incident"
                        />
                    </div>

                    <div className="col-md-6">
                        <DynamicSelect
                            label="Catégorie de plainte"
                            name="categorie"
                            categoryId="categorie"
                            value={formData.categorie}
                            handleChange={handleInputChange}
                            error={errors.categorie}
                            required
                            tooltip="Type de réclamation"
                        />
                    </div>
                    <div className="col-md-6">
                        <DynamicSelect
                            label="Élément"
                            name="element"
                            categoryId="element"
                            value={formData.element}
                            handleChange={handleInputChange}
                            error={errors.element}
                            required
                            tooltip="Précisez l'élément concerné par votre réclamation"
                        />
                    </div>

                    <div className="col-md-6">
                        <Input
                            label="Objet de la plainte"
                            name="objetPlainte"
                            value={formData.objetPlainte}
                            handleChange={handleInputChange}
                            error={errors.objetPlainte}
                            required
                            tooltip="Résumé bref de votre réclamation"
                        />
                    </div>
                    <div className="col-md-6">
                        <Input
                            label="Montant réclamé"
                            name="montantReclame"
                            type="number"
                            value={formData.montantReclame}
                            handleChange={handleInputChange}
                            error={errors.montantReclame}
                            tooltip="Montant concerné par la réclamation"
                            min="0"  // Ajout de cette ligne pour empêcher les valeurs négatives
                        />
                    </div>

                    <div className="col-md-6">
                        <Input
                            label="Date incident"
                            name="dateIncident"
                            type="date"
                            value={formData.dateIncident}
                            handleChange={handleInputChange}
                            error={errors.dateIncident}
                            required
                            tooltip="Date à laquelle s'est produit l'incident"
                            max={new Date().toISOString().split('T')[0]}
                        />
                    </div>
                    <div className="col-md-6">
                        <DynamicSelect
                            label="Devise"
                            name="devise"
                            categoryId="devise"
                            value={formData.devise}
                            handleChange={handleInputChange}
                            error={errors.devise}
                            tooltip="Devise du montant réclamé"
                        />
                    </div>

                    <div className="col-12">
                        <div className="mb-3">
                            <label className="form-label text-start w-100">
                                Pièces jointes
                                <span className="ms-2 text-muted">(Facultatif)</span>
                            </label>
                            <div className="d-flex align-items-center">
                                <input
                                    type="file"
                                    className="form-control"
                                    onChange={handleFileChange}
                                    multiple
                                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                />
                            </div>
                            <small className="text-muted">
                                Formats acceptés: PDF, JPG, PNG, DOC, DOCX. Taille max: 5 MB par fichier
                            </small>
                        </div>
                    </div>

                    <div className="col-12">
                        <TextArea
                            label="Description"
                            name="description"
                            value={formData.description}
                            handleChange={handleInputChange}
                            error={errors.description}
                            required
                            tooltip="Décrivez en détail la situation et le problème rencontré"
                            minLength={50}
                            maxLength={2000}
                            helperText="Soyez le plus précis possible dans votre description"
                        />
                    </div>

                    <div className="col-12">
                        <TextArea
                            label="Attente"
                            name="attente"
                            value={formData.attente}
                            handleChange={handleInputChange}
                            error={errors.attente}
                            required
                            tooltip="Expliquez ce que vous attendez comme résolution"
                            minLength={20}
                            maxLength={1000}
                            helperText="Précisez vos attentes concernant le traitement de votre réclamation"
                        />
                    </div>

                    <div className="col-12">
                        <Radio
                            label="S'agit-il d'une relance à une réclamation déjà introduite ?"
                            name="isRelance"
                            options={[
                                { value: 'oui', label: 'Oui' },
                                { value: 'non', label: 'Non' }
                            ]}
                            value={formData.isRelance}
                            handleChange={handleInputChange}
                            error={errors.isRelance}
                            required
                        />
                    </div>

                    {/* Section conditionnelle pour les relances */}
                    {(fieldVisibility.datereclamationinitiale || fieldVisibility.modeintroductioninitiale) && (
                        <div className="col-12 mt-3">
                            <div className="relance-section p-3 border rounded bg-light">
                                <h5 className="mb-3">Informations sur la réclamation initiale</h5>
                                <div className="row g-3">
                                    {fieldVisibility.datereclamationinitiale && (
                                        <div className="col-md-6">
                                            <Input
                                                label="Date de la réclamation initiale"
                                                name="dateReclamationInitiale"
                                                type="date"
                                                value={formData.dateReclamationInitiale}
                                                handleChange={handleInputChange}
                                                error={errors.dateReclamationInitiale}
                                                required
                                                tooltip="Date à laquelle vous avez introduit votre réclamation initiale"
                                                max={new Date().toISOString().split('T')[0]}
                                            />
                                        </div>
                                    )}

                                    {fieldVisibility.modeintroductioninitiale && (
                                        <div className="col-md-6">
                                            <DynamicSelect
                                                label="Comment avez-vous introduit la réclamation initiale ?"
                                                name="modeIntroductionInitiale"
                                                categoryId="modeIntroduction"
                                                value={formData.modeIntroductionInitiale}
                                                handleChange={handleInputChange}
                                                error={errors.modeIntroductionInitiale}
                                                required
                                                tooltip="Mode par lequel vous avez fait votre première réclamation"
                                            />
                                        </div>
                                    )}

                                    {/* Champ conditionnel pour l'agence */}
                                    {fieldVisibility.precisionagence && (
                                        <div className="col-md-12">
                                            <DynamicSelect
                                                label="Précisez l'agence"
                                                name="precisionAgence"
                                                categoryId="agence"
                                                value={formData.precisionAgence}
                                                handleChange={handleInputChange}
                                                error={errors.precisionAgence}
                                                required
                                                tooltip="Sélectionnez l'agence où vous avez déposé votre réclamation initiale"
                                            />
                                        </div>
                                    )}

                                    {/* Champ conditionnel pour le gestionnaire */}
                                    {fieldVisibility.nomgestionnaire && (
                                        <div className="col-md-12">
                                            <Input
                                                label="Précisez le nom du Gestionnaire"
                                                name="nomGestionnaire"
                                                value={formData.nomGestionnaire}
                                                handleChange={handleInputChange}
                                                error={errors.nomGestionnaire}
                                                required
                                                tooltip="Nom de votre gestionnaire de compte"
                                                helperText="Indiquez le nom complet de votre gestionnaire"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </Section>

            <div className="d-flex justify-content-end mt-4">
                <button type="submit" className="brand-button">
                    Suivant
                </button>
            </div>
            <style>{`
            .invalid-feedback {
                display: block !important;
                color: #dc3545;
                margin-top: 0.25rem;
            }

            .is-invalid {
                border-color: #dc3545 !important;
                box-shadow: 0 0 0 0.1rem rgba(220, 53, 69, 0.25);
            }

            select.is-invalid {
                background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' width='12' height='12' fill='none' stroke='%23dc3545'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath stroke-linejoin='round' d='M5.8 3.6h.4L6 6.5z'/%3e%3ccircle cx='6' cy='8.2' r='.6' fill='%23dc3545' stroke='none'/%3e%3c/svg%3e");
                background-repeat: no-repeat;
                background-position: right calc(0.375em + 0.1875rem) center;
                background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem);
                padding-right: 4.125rem !important;
            }

            .form-group {
                margin-bottom: 1rem;
                position: relative;
            }
            
            .tooltip-custom {
                position: absolute;
                z-index: 1000;
                background-color: #fff;
                border: 1px solid #dee2e6;
                border-radius: 4px;
                padding: 8px;
                font-size: 0.875rem;
                box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                max-width: 250px;
            }

            .cursor-pointer {
                cursor: pointer;
            }

            .brand-button {
                background-color: #0A6EBD;
                color: white;
                border: none;
                padding: 8px 24px;
                border-radius: 4px;
                font-weight: 500;
                transition: background-color 0.2s;
            }

            .brand-button:hover {
                background-color: #085494;
            }
            
            .relance-section {
                background-color: #f8f9fa;
                border: 1px solid #dce4ec;
                border-radius: 5px;
                transition: all 0.3s ease;
            }
            
            .relance-section h5 {
                color: #0A6EBD;
                font-size: 1.1rem;
                font-weight: 600;
            }
            `}</style>
        </form>
    );
};

export default FormStep;